/**
 * Confirmation for removing a box (#804).
 *
 * `DELETE /machines/:id` does more than drop a row: it writes a tombstone
 * keyed by the box's SideroLink node UUID, so the box's next Provision call
 * is refused (410) and it cannot rejoin any site until the tombstone is
 * cleared (`POST /machines/re-enroll`, #121). The "Remove" button on the old
 * Provisioning Nodes card did that with no confirmation at all.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { machinesApi } from '@/api/client';
import { recoveryErrorMessage } from '@/lib/boxRecovery';
import type { Machine } from '@/types';

export function RemoveMachineDialog({
  machine,
  onClose,
}: {
  machine: Machine;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => machinesApi.delete(machine.id),
    onSuccess: () => {
      // ['machines'] is a prefix of every per-site ['machines', siteId] key.
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      if (machine.siteId) queryClient.invalidateQueries({ queryKey: ['cluster-nodes', machine.siteId] });
      onClose();
    },
  });
  const err = remove.isError ? recoveryErrorMessage(remove.error, 'remove') : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Remove ${machine.hostname}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-status-error" />
            <h3 className="text-silver-bright font-semibold">Remove box</h3>
          </div>
          <button type="button" onClick={onClose} className="text-silver-muted hover:text-silver-bright" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3 text-sm text-silver-muted">
          <p>
            Remove <span className="font-mono text-silver-bright">{machine.hostname}</span> from Jeket?
          </p>
          <ul className="list-disc pl-5 space-y-1 text-xs">
            <li>Jeket deletes its record of this box{machine.siteId ? ' and it leaves the site' : ''}.</li>
            <li className="text-status-error">
              The box is <strong>blocked from rejoining any site</strong>: when it next contacts the Jeket
              cloud it is refused, even after a reflash. Only Jeket support can lift the block.
            </li>
            <li>Nothing on the box itself is erased or shut down.</li>
          </ul>
          <p className="text-xs">
            Use this only for a box that is leaving for good. To set a box up again, use
            Reset to maintenance or Reinstall instead.
          </p>
          {err && (
            <div className="text-xs text-status-error bg-status-error/10 border border-status-error/30 rounded px-3 py-2 space-y-1">
              <p>{err.message}</p>
              {err.hint && <p className="text-silver-muted">{err.hint}</p>}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            data-testid="confirm-remove-machine"
            className="px-4 py-2 bg-status-error text-white text-sm font-medium rounded-lg hover:bg-status-error/80 transition-colors disabled:opacity-50"
          >
            {remove.isPending ? 'Removing…' : 'Remove and block'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RemoveMachineDialog;
